import { IEquipment } from '@/pages/Equipment/equipment.types.ts'
import http, { handleHttpError, handleHttpResponse } from '@/services/http'
import { HTTPResponse } from '@/services/http/http.types.ts'
import { IAvatar, IUserInfo, IUserSettings } from '../Users/users.types'
import { IUserNotificationChannel, IUserRouteHistory } from './user.types'

const checkIfSubscribedForEquipmentEvents = (
  equipmentId: IEquipment['id']
): Promise<HTTPResponse<{ subscribed: boolean }>> => {
  return http
    .get('/nsi/v1/self/equipment-subscriptions/' + equipmentId)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getEquipmentSubscriptionsList = (): Promise<HTTPResponse<{ equipmentIds: Array<IEquipment['id']> }>> => {
  return http.get('/nsi/v1/self/equipment-subscriptions/').then(handleHttpResponse).catch(handleHttpError)
}

const subscribeForAllEquipmentEvents = (): Promise<HTTPResponse<{ equipmentIds: Array<IEquipment['id']> }>> => {
  return http.post('/nsi/v1/self/equipment-subscriptions/subscribe/all').then(handleHttpResponse).catch(handleHttpError)
}

const unsubscribeFromAllEquipmentEvents = (): Promise<HTTPResponse<{ subscribed: boolean }>> => {
  return http.post('/nsi/v1/self/equipment-subscriptions/subscribe/all').then(handleHttpResponse).catch(handleHttpError)
}

const updateSelfInfo = (userInfo: Omit<IUserInfo, 'avatar'>): Promise<HTTPResponse<Omit<IUserInfo, 'avatar'>>> => {
  return http.patch('/nsi/v1/self/self-info', userInfo).then(handleHttpResponse).catch(handleHttpError)
}

const updateSelfSettings = (settings: IUserSettings): Promise<HTTPResponse<IUserSettings>> => {
  return http.patch('/nsi/v1/self/settings', settings).then(handleHttpResponse).catch(handleHttpError)
}

const updateSelfAvatar = (file: File): Promise<HTTPResponse<IAvatar>> => {
  const data = new FormData()
  data.append('document', file)
  return http.put('/nsi/v1/self/self-info/avatar', data).then(handleHttpResponse).catch(handleHttpError)
}

const subscribeToEquipmentEventList = (
  equipmentIds: IEquipment['id'][]
): Promise<HTTPResponse<{ subscribed: boolean }>> => {
  return http
    .post('/nsi/v1/self/equipment-subscriptions/', { equipmentIds })
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const unsubscribeFromEquipmentEventList = (
  equipmentIds: IEquipment['id'][]
): Promise<HTTPResponse<{ subscribed: boolean }>> => {
  return http
    .post('/nsi/v1/self/equipment-subscriptions/unsubscribe/', { equipmentIds })
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const addEquipmentToFavourite = (equipmentId: IEquipment['id']): Promise<HTTPResponse> => {
  return http
    .post('/nsi/v1/self/favourite-equipment/' + equipmentId)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const removeEquipmentFromFavourite = (equipmentId: IEquipment['id']): Promise<HTTPResponse> => {
  return http
    .delete('/nsi/v1/self/favourite-equipment/' + equipmentId)
    .then(handleHttpResponse)
    .catch(handleHttpError)
}

const getSelfRoutesStats = (): Promise<HTTPResponse<IUserRouteHistory[]>> => {
  return http.get('/nsi/v1/self/routes-history/stats').then(handleHttpResponse).catch(handleHttpError)
}

const addHistoryRoute = (routeName: string): Promise<HTTPResponse> => {
  return http.post('/nsi/v1/self/routes-history/add', { routeName }).then(handleHttpResponse).catch(handleHttpError)
}

const loadNotificationChannels = (): Promise<HTTPResponse<{ channels: IUserNotificationChannel[] }>> => {
  return http.get('/ws/v1/notifications/channels').then(handleHttpResponse).catch(handleHttpError)
}

const putNotificationChannel = (
  dto: Partial<Pick<IUserNotificationChannel, 'channel' | 'telegramChatId' | 'emailAddress' | 'enabled'>>
): Promise<HTTPResponse<IUserNotificationChannel>> => {
  return http.put('/ws/v1/notifications/channels/:channel', dto).then(handleHttpResponse).catch(handleHttpError)
}

const userApi = {
  getSelfRoutesStats,
  addHistoryRoute,
  addEquipmentToFavourite,
  removeEquipmentFromFavourite,
  checkIfSubscribedForEquipmentEvents,
  subscribeToEquipmentEventList,
  unsubscribeFromEquipmentEventList,
  getEquipmentSubscriptionsList,
  subscribeForAllEquipmentEvents,
  unsubscribeFromAllEquipmentEvents,
  updateSelfInfo,
  updateSelfAvatar,
  updateSelfSettings,
  loadNotificationChannels,
  putNotificationChannel,
}
export default userApi
